import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  Notification,
  NotificationFilters,
  CreateNotificationDto,
  UpdateNotificationDto,
  DeliveryStatsResponse,
  SendNotificationResponse,
  MyNotificationsFilters,
} from '@tco/shared-types'

export interface PaginatedNotifications {
  data: Notification[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const NOTIFICATIONS_QUERY_KEY = 'notifications'
const MY_NOTIFICATIONS_QUERY_KEY = 'my-notifications'
const DELIVERY_STATS_QUERY_KEY = 'delivery-stats'

// ==================== Admin Hooks ====================

/**
 * Get paginated list of all notifications (admin)
 */
export const useNotifications = (filters: NotificationFilters = {}) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedNotifications> => {
      const response = await apiClient.getNotifications(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

/**
 * Get single notification by ID
 */
export const useNotification = (id: string) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, id],
    queryFn: async (): Promise<Notification> => {
      const response = await apiClient.getNotification(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

/**
 * Create new notification
 */
export const useCreateNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateNotificationDto): Promise<Notification> => {
      const response = await apiClient.createNotification(data)
      // Backend returns Notification object directly
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

/**
 * Update existing notification
 */
export const useUpdateNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateNotificationDto }) => {
      const response = await apiClient.updateNotification(id, data)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY, id] })
    },
  })
}

/**
 * Delete notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deleteNotification(id)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

/**
 * Send notification (trigger delivery)
 */
export const useSendNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<SendNotificationResponse> => {
      const response = await apiClient.sendNotification(id)
      return response
    },
    onSuccess: (_, id) => {
      // Invalidate notifications list and specific notification
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY, id] })
      // Also invalidate delivery stats
      queryClient.invalidateQueries({ queryKey: [DELIVERY_STATS_QUERY_KEY, id] })
    },
  })
}

/**
 * Get delivery statistics for notification
 */
export const useDeliveryStats = (notificationId: string) => {
  return useQuery({
    queryKey: [DELIVERY_STATS_QUERY_KEY, notificationId],
    queryFn: async (): Promise<DeliveryStatsResponse> => {
      const response = await apiClient.getNotificationDeliveryStats(notificationId)
      // API returns data directly, not wrapped in { data: ... }
      return response as unknown as DeliveryStatsResponse
    },
    enabled: !!notificationId,
    staleTime: 10 * 1000, // 10 seconds (stats update frequently)
    refetchInterval: 15 * 1000, // Auto-refresh every 15 seconds
  })
}

// ==================== User Hooks ====================

/**
 * Get my notifications (for current user)
 */
export const useMyNotifications = (filters: MyNotificationsFilters = {}) => {
  return useQuery({
    queryKey: [MY_NOTIFICATIONS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedNotifications> => {
      const response = await apiClient.getMyNotifications(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
    retry: 3,
  })
}

/**
 * Mark notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.markNotificationAsRead(id)
      return response
    },
    onSuccess: () => {
      // Invalidate my notifications list
      queryClient.invalidateQueries({ queryKey: [MY_NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

/**
 * Mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.markAllNotificationsAsRead()
      return response
    },
    onSuccess: () => {
      // Invalidate my notifications list
      queryClient.invalidateQueries({ queryKey: [MY_NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

// ==================== Utility Hooks ====================

/**
 * Combined hook for create + send in one action
 */
export const useCreateAndSendNotification = () => {
  const createMutation = useCreateNotification()
  const sendMutation = useSendNotification()

  return {
    mutateAsync: async (data: CreateNotificationDto) => {
      // First create the notification
      const notification = await createMutation.mutateAsync(data)

      // Then send it immediately
      const sendResult = await sendMutation.mutateAsync(notification.id)

      return {
        notification,
        sendResult,
      }
    },
    isLoading: createMutation.isPending || sendMutation.isPending,
    error: createMutation.error || sendMutation.error,
  }
}

/**
 * Get unread notifications count (for badge)
 */
export const useUnreadNotificationsCount = () => {
  return useQuery({
    queryKey: [MY_NOTIFICATIONS_QUERY_KEY, 'unread-count'],
    queryFn: async (): Promise<number> => {
      const response = await apiClient.getMyNotifications({
        is_read: false,
        limit: 1, // We only need the count
      })
      return response.total || 0
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  })
}
